import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

import { toUTCDayEnd, toUTCDayStart } from './toUTCDayBounds'

export const TopPostsDocument = graphql(/* GraphQL */ `
  query TopPosts($input: TopPostsInput!, $first: Int, $after: String) {
    topPosts(input: $input, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          text
          sentAt
          channelId
          channelService
          externalLink
          allowedActions
          channel {
            id
            name
            service
            serviceId
            avatar
            allowedActions
          }
          metadata {
            ... on CommonPostMetadata {
              type
            }
          }
          tags {
            id
            name
            color
          }
          assets {
            id
            type
            thumbnail
          }
          metrics {
            type
            name
            value
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`)

type TopPostsResult = ResultOf<typeof TopPostsDocument>

export type TopPost = TopPostsResult['topPosts']['edges'][number]['node']

type TopPostsVariables = VariablesOf<typeof TopPostsDocument>

/**
 * The date/channel/tag filter every Insights query is scoped by. Derived from
 * the `input.filter` variable on {@link TopPostsDocument}.
 */
export type InsightsFilterInput = TopPostsVariables['input']['filter']

/** Tag filter shape, derived from `filter.tags` on {@link TopPostsDocument}. */
export type TagComparator = NonNullable<InsightsFilterInput['tags']>

/**
 * A post format, derived from `filter.postTypes` on {@link TopPostsDocument}.
 * The schema rejects `carousel` and `thread`, so a selection is validated
 * server-side rather than narrowed here. @see supportedPostTypesForService
 */
export type PostType = NonNullable<InsightsFilterInput['postTypes']>[number]

/** Sort field + direction, derived from `input.sort` on {@link TopPostsDocument}. */
export type TopPostsSortInput = TopPostsVariables['input']['sort']

/** A sortable post metric. @see {@link TopPostsDocument} */
export type TopPostsSortBy = TopPostsSortInput['field']

/** Sort direction. @see {@link TopPostsDocument} */
export type SortDirection = TopPostsSortInput['direction']

interface UseTopPostsOptions {
  /**
   * The primary window, always — even while the comparison window below is the
   * one being listed. The free-plan history cap is measured against this range
   * and never against the comparison, so a comparison reaching past the cap is
   * served rather than rejected (CORE-7746). Swapping the comparison window in
   * here instead is what broke Performance per Post's "Previous Period" tab for
   * every free organization on the default 30-day range.
   */
  filter: InsightsFilterInput
  sort: TopPostsSortInput
  /**
   * Lists the posts sent in this window instead of `filter`'s. Both bounds or
   * neither — one alone is ignored, matching the schema's contract.
   */
  comparisonStartDate?: Date | null
  comparisonEndDate?: Date | null
  first?: number
  after?: string | null
  /** Skip the query entirely (e.g. the actor can't view insights here). */
  skip?: boolean
}

interface UseTopPostsResult {
  topPosts: TopPost[]
  totalCount: number
  hasNextPage: boolean
  endCursor: string | null
  loading: boolean
  error: ErrorLike | undefined
}

export function useTopPosts({
  filter,
  sort,
  comparisonStartDate,
  comparisonEndDate,
  first,
  after,
  skip = false,
}: UseTopPostsOptions): UseTopPostsResult {
  const comparison =
    comparisonStartDate && comparisonEndDate
      ? {
          comparisonStartDate: toUTCDayStart(comparisonStartDate),
          comparisonEndDate: toUTCDayEnd(comparisonEndDate),
        }
      : {}

  const { data, previousData, loading, error } = useQuery(TopPostsDocument, {
    variables: {
      input: { filter, sort, ...comparison },
      first,
      ...(after ? { after } : {}),
    },
    fetchPolicy: 'cache-first',
    skip:
      skip || !filter.organizationId || !filter.startDate || !filter.endDate,
  })

  // Use previous data while refetching to avoid layout jumps
  const effectiveData = data ?? previousData
  const connection = effectiveData?.topPosts

  return {
    topPosts: connection?.edges.map((edge) => edge.node) ?? [],
    totalCount: connection?.totalCount ?? 0,
    hasNextPage: connection?.pageInfo.hasNextPage ?? false,
    endCursor: connection?.pageInfo.endCursor ?? null,
    loading,
    error,
  }
}
